// =============================================================================
// Admin Campaign Detail Page (Phase II: Fundraising CRM)
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  EyeIcon,
  TrashIcon,
  UserGroupIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { Card, Button } from '../../../components/ui';
import { fundraisingService } from '../../../services/fundraising.service';
import { getApiError } from '../../../services/api';
import { usePermissions } from '../../../hooks/usePermissions';
import type {
  EmailCampaign,
  CampaignPreview,
  CampaignStats,
  CampaignRecipient,
  SegmentCriteria,
  PipelineStage,
} from '../../../types/fundraising';
import {
  CAMPAIGN_STATUS_LABELS,
  PIPELINE_STAGE_LABELS,
} from '../../../types/fundraising';

// =============================================================================
// Status Badge Colors
// =============================================================================

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SCHEDULED: 'bg-blue-100 text-blue-700',
  SENDING: 'bg-yellow-100 text-yellow-700',
  SENT: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

// =============================================================================
// Component
// =============================================================================

export function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { hasPermission, isLoaded: permsLoaded } = usePermissions();
  const canEdit = hasPermission('fundraising', 'edit');
  const canDelete = hasPermission('fundraising', 'delete');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [campaign, setCampaign] = useState<EmailCampaign | null>(null);
  const [preview, setPreview] = useState<CampaignPreview | null>(null);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [recipients, setRecipients] = useState<CampaignRecipient[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'edit' | 'preview' | 'stats'>('edit');
  const [hasChanges, setHasChanges] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [excludeUnsubscribed, setExcludeUnsubscribed] = useState(true);
  const [segmentTags, setSegmentTags] = useState<string[]>([]);
  const [segmentStages, setSegmentStages] = useState<PipelineStage[]>([]);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Send confirmation
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [sendConfirmText, setSendConfirmText] = useState('');

  const loadCampaign = useCallback(async () => {
    if (!campaignId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fundraisingService.getCampaign(campaignId);
      setCampaign(data);
      setName(data.name);
      setSubject(data.subject);
      setBodyHtml(data.bodyHtml);
      setExcludeUnsubscribed(data.excludeUnsubscribed);
      if (data.segmentCriteria) {
        setSegmentTags(data.segmentCriteria.tags || []);
        setSegmentStages(data.segmentCriteria.stages || []);
      }
      setHasChanges(false);

      // Load additional data if campaign is sent
      if (data.status === 'SENT') {
        const [statsData, recipientsData] = await Promise.all([
          fundraisingService.getCampaignStats(campaignId),
          fundraisingService.getCampaignRecipients(campaignId),
        ]);
        setStats(statsData);
        setRecipients(recipientsData.recipients);
        setActiveTab('stats');
      }
    } catch (err) {
      console.error('Failed to load campaign:', err);
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  }, [campaignId]);

  const loadPreview = useCallback(async () => {
    if (!campaignId) return;
    try {
      const data = await fundraisingService.previewCampaignRecipients(campaignId);
      setPreview(data);
    } catch (err) {
      console.error('Failed to load preview:', err);
    }
  }, [campaignId]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  useEffect(() => {
    if (activeTab === 'preview' && !preview) {
      loadPreview();
    }
  }, [activeTab, preview, loadPreview]);

  const handleSave = async () => {
    if (!campaignId || !hasChanges) return;
    setIsSaving(true);
    setError(null);
    try {
      const segmentCriteria: SegmentCriteria = {};
      if (segmentTags.length > 0) segmentCriteria.tags = segmentTags;
      if (segmentStages.length > 0) segmentCriteria.stages = segmentStages;

      const updated = await fundraisingService.updateCampaign(campaignId, {
        name,
        subject,
        bodyHtml,
        excludeUnsubscribed,
        segmentCriteria: Object.keys(segmentCriteria).length > 0 ? segmentCriteria : undefined,
      });
      setCampaign(updated);
      setHasChanges(false);
      setPreview(null); // Reset preview since criteria may have changed
    } catch (err) {
      console.error('Failed to save campaign:', err);
      setError(getApiError(err).message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendClick = () => {
    if (!campaignId) return;
    if (!subject.trim()) {
      setError('Please add an email subject before sending');
      return;
    }
    if (!bodyHtml.trim()) {
      setError('Please add email content before sending');
      return;
    }
    setSendConfirmText('');
    setShowSendConfirm(true);
  };

  const handleSendConfirm = async () => {
    if (!campaignId || sendConfirmText.toLowerCase() !== 'proceed') return;
    setShowSendConfirm(false);
    setIsSending(true);
    setError(null);
    try {
      // Save any pending changes first
      if (hasChanges) {
        await handleSave();
      }
      const sent = await fundraisingService.sendCampaign(campaignId);
      setCampaign(sent);
      setActiveTab('stats');
      // Reload to get stats
      const [statsData, recipientsData] = await Promise.all([
        fundraisingService.getCampaignStats(campaignId),
        fundraisingService.getCampaignRecipients(campaignId),
      ]);
      setStats(statsData);
      setRecipients(recipientsData.recipients);
    } catch (err) {
      console.error('Failed to send campaign:', err);
      setError(getApiError(err).message);
    } finally {
      setIsSending(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmText('');
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!campaignId || deleteConfirmText.toLowerCase() !== 'proceed') return;
    setIsDeleting(true);
    try {
      await fundraisingService.deleteCampaign(campaignId);
      navigate('/admin/fundraising/campaigns');
    } catch (err) {
      console.error('Failed to delete campaign:', err);
      setError(getApiError(err).message);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Track changes
  useEffect(() => {
    if (!campaign) return;
    const changed =
      name !== campaign.name ||
      subject !== campaign.subject ||
      bodyHtml !== campaign.bodyHtml ||
      excludeUnsubscribed !== campaign.excludeUnsubscribed;
    setHasChanges(changed);
  }, [name, subject, bodyHtml, excludeUnsubscribed, campaign]);

  // Permission guard - early return after all hooks
  if (permsLoaded && !hasPermission('fundraising', 'view')) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <ShieldExclamationIcon className="h-12 w-12 text-gray-400 mb-3" />
        <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
        <p className="mt-1 text-sm text-gray-500">You do not have permission to view this page.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-96 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="space-y-6">
        <Link
          to="/admin/fundraising/campaigns"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Campaigns
        </Link>
        <Card padding="lg">
          <div className="text-center py-12">
            <EnvelopeIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              {error || 'Campaign not found'}
            </h3>
            <Button className="mt-4" onClick={() => navigate('/admin/fundraising/campaigns')}>
              Return to Campaigns
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const isDraft = campaign.status === 'DRAFT';

  const tabs = [
    { id: 'edit', label: isDraft ? 'Edit' : 'Details', icon: EnvelopeIcon },
    { id: 'preview', label: 'Recipients', icon: UserGroupIcon },
    ...(campaign.status === 'SENT' ? [{ id: 'stats', label: 'Statistics', icon: ChartBarIcon }] : []),
  ] as const;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/fundraising/campaigns"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[campaign.status]}`}>
                {CAMPAIGN_STATUS_LABELS[campaign.status]}
              </span>
            </div>
            {campaign.sentAt && (
              <p className="text-sm text-gray-500 mt-1">Sent {formatDate(campaign.sentAt)}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isDraft && (
            <>
              {canDelete && (
                <Button variant="secondary" onClick={handleDeleteClick}>
                  <TrashIcon className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="secondary"
                  onClick={handleSave}
                  disabled={!hasChanges || isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
              {canEdit && (
                <Button onClick={handleSendClick} disabled={isSending}>
                  <PaperAirplaneIcon className="h-4 w-4 mr-1" />
                  {isSending ? 'Sending...' : 'Send Campaign'}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as 'edit' | 'preview' | 'stats')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'edit' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Editor */}
          <Card padding="lg" className="lg:col-span-2">
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!isDraft}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={!isDraft}
                  placeholder="Enter email subject line"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                />
              </div>

              <div>
                <label htmlFor="bodyHtml" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Content (HTML) *
                </label>
                <textarea
                  id="bodyHtml"
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  disabled={!isDraft}
                  rows={15}
                  placeholder="Enter email content in HTML format...&#10;&#10;Available placeholders:&#10;{{firstName}} - Contact's first name&#10;{{lastName}} - Contact's last name&#10;{{fullName}} - Full name&#10;{{email}} - Email address&#10;{{company}} - Company name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm disabled:bg-gray-50"
                />
              </div>
            </div>
          </Card>

          {/* Sidebar - Targeting */}
          <Card padding="lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Targeting</h3>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="excludeUnsubscribed"
                  checked={excludeUnsubscribed}
                  onChange={(e) => setExcludeUnsubscribed(e.target.checked)}
                  disabled={!isDraft}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="excludeUnsubscribed" className="text-sm text-gray-700">
                  Exclude unsubscribed contacts
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Pipeline Stage
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {(Object.keys(PIPELINE_STAGE_LABELS) as PipelineStage[]).map((stage) => (
                    <label key={stage} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={segmentStages.includes(stage)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSegmentStages([...segmentStages, stage]);
                          } else {
                            setSegmentStages(segmentStages.filter((s) => s !== stage));
                          }
                        }}
                        disabled={!isDraft}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">{PIPELINE_STAGE_LABELS[stage]}</span>
                    </label>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {segmentStages.length === 0
                    ? 'No filter - all contacts will receive the email'
                    : `Only contacts in selected stages will receive the email`}
                </p>
              </div>

              {preview && (
                <div className="pt-4 border-t">
                  <div className="flex items-center gap-2 text-sm">
                    <UserGroupIcon className="h-5 w-5 text-gray-400" />
                    <span className="text-gray-700">
                      <strong>{preview.count}</strong> recipients match criteria
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'preview' && (
        <Card padding="lg">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Recipients {preview ? `(${preview.count})` : ''}
            </h2>
            <Button variant="secondary" size="sm" onClick={loadPreview}>
              <EyeIcon className="h-4 w-4 mr-1" />
              Refresh
            </Button>
          </div>

          {preview ? (
            preview.count > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Name</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Email</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Company</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.contacts.slice(0, 50).map((contact) => (
                      <tr key={contact.id} className="border-b border-gray-100">
                        <td className="py-3 px-4 text-sm text-gray-900">
                          {contact.firstName} {contact.lastName}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">{contact.email}</td>
                        <td className="py-3 px-4 text-sm text-gray-500">{contact.company || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.count > 50 && (
                  <p className="text-sm text-gray-500 mt-4 text-center">
                    Showing first 50 of {preview.count} recipients
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto" />
                <p className="mt-2 text-sm text-gray-500">
                  No contacts match the targeting criteria.
                </p>
              </div>
            )
          ) : (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
              <p className="mt-2 text-sm text-gray-500">Loading recipients...</p>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'stats' && stats && (
        <div className="space-y-6">
          {/* Stats Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card padding="sm">
              <p className="text-sm text-gray-500">Sent</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalSent.toLocaleString()}</p>
            </Card>
            <Card padding="sm">
              <p className="text-sm text-gray-500">Opened</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.totalOpened.toLocaleString()}
                <span className="text-sm font-normal text-gray-500 ml-1">({stats.openRate}%)</span>
              </p>
            </Card>
            <Card padding="sm">
              <p className="text-sm text-gray-500">Clicked</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.totalClicked.toLocaleString()}
                <span className="text-sm font-normal text-gray-500 ml-1">({stats.clickRate}%)</span>
              </p>
            </Card>
            <Card padding="sm">
              <p className="text-sm text-gray-500">Bounced</p>
              <p className="text-2xl font-bold text-red-600">
                {stats.totalBounced.toLocaleString()}
                <span className="text-sm font-normal text-gray-500 ml-1">({stats.bounceRate}%)</span>
              </p>
            </Card>
          </div>

          {/* Recipients List */}
          <Card padding="lg">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Delivery Status</h2>
            {recipients.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Recipient</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Sent</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Opened</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Clicked</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((recipient) => (
                      <tr key={recipient.id} className="border-b border-gray-100">
                        <td className="py-3 px-4">
                          <Link
                            to={`/admin/fundraising/contacts/${recipient.contact.id}`}
                            className="text-sm font-medium text-primary-600 hover:text-primary-700"
                          >
                            {recipient.contact.firstName} {recipient.contact.lastName}
                          </Link>
                          <p className="text-xs text-gray-500">{recipient.contact.email}</p>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {formatDate(recipient.sentAt)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {recipient.openedAt ? (
                            <CheckCircleIcon className="h-5 w-5 text-green-500 mx-auto" />
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {recipient.clickedAt ? (
                            <span className="text-sm text-blue-600">{recipient.clickCount}</span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {recipient.bouncedAt ? (
                            <span className="inline-flex items-center gap-1 text-xs text-red-600">
                              <XCircleIcon className="h-4 w-4" />
                              Bounced
                            </span>
                          ) : recipient.unsubscribedAt ? (
                            <span className="text-xs text-gray-500">Unsubscribed</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-green-600">
                              <CheckCircleIcon className="h-4 w-4" />
                              Delivered
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No recipients found.</p>
            )}
          </Card>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Campaign</h3>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              You are about to permanently delete this campaign:
            </p>
            <p className="text-sm font-medium text-gray-900 mb-4 p-2 bg-gray-50 rounded-lg">
              {name || campaign?.name || 'Untitled Campaign'}
            </p>
            <p className="text-sm text-red-600 mb-4">
              This action cannot be undone.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> below to confirm deletion:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteConfirmText.toLowerCase() !== 'proceed' || isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Deleting...
                  </>
                ) : (
                  'Delete Campaign'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Confirmation Modal */}
      {showSendConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-full">
                <PaperAirplaneIcon className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Send Campaign</h3>
            </div>

            <p className="text-sm text-gray-600 mb-2">
              You are about to send this campaign{preview ? ` to ${preview.count} recipients` : ''}:
            </p>
            <p className="text-sm font-medium text-gray-900 mb-4 p-2 bg-gray-50 rounded-lg">
              {name || campaign?.name || 'Untitled Campaign'}
            </p>
            <p className="text-sm text-amber-600 mb-4">
              This action cannot be undone. Emails will be sent immediately.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="font-bold">proceed</span> below to confirm:
              </label>
              <input
                type="text"
                value={sendConfirmText}
                onChange={(e) => setSendConfirmText(e.target.value)}
                placeholder="Type 'proceed' to confirm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowSendConfirm(false);
                  setSendConfirmText('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendConfirm}
                disabled={sendConfirmText.toLowerCase() !== 'proceed'}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Send Campaign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignDetailPage;
